import { getApolloContext } from '@apollo/client/react';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AgentStreamEvent, AgentUiCommand } from './agent-api';
import type { PageRecordType } from './page-context';

/**
 * The dashboard half of the agent's UI commands: open a page, point at a
 * record, reload what is on screen after a write. Mirrors NAVIGABLE_ROOTS in
 * apps/panel-api/src/agent/entities/ui-command.ts and checks every path again
 * here, so a command the server let through by mistake still goes nowhere.
 */
const NAVIGABLE_ROOTS = [
  'assistant',
  'dashboard',
  'renewals',
  'finance',
  'tasks',
  'clients',
  'subscriptions',
  'prospects',
  'contacts',
  'pipeline',
  'catalog',
  'discounts',
  'market-config',
  'product-config',
  'settings',
  'usage',
  'import',
  'okr',
  'users',
  'account',
];

const NAVIGABLE_PATH = new RegExp(
  `^/(?:${NAVIGABLE_ROOTS.join('|')})(?:/[A-Za-z0-9\\-_]{1,64}){0,4}$`,
);

export function isNavigablePath(path: unknown): path is string {
  return typeof path === 'string' && path.length <= 200 && NAVIGABLE_PATH.test(path);
}

const RECORD_PATHS: Record<PageRecordType, (id: string) => string> = {
  client: (id) => `/clients/${id}`,
  subscription: (id) => `/subscriptions/${id}`,
  module: (id) => `/catalog/modules/${id}`,
  bundle: (id) => `/catalog/bundles/${id}`,
};

export function recordPath(recordType: PageRecordType, recordId: string): string | undefined {
  const path = RECORD_PATHS[recordType]?.(recordId);
  return isNavigablePath(path) ? path : undefined;
}

/** How long a highlighted record stays marked. Matches the CSS transition. */
const HIGHLIGHT_MS = 2400;
/** How long to wait for a just-opened page to render the record. */
const HIGHLIGHT_LOOKUP_MS = 600;

/**
 * Pages mark a record they show with `data-agent-record="<type>:<id>"`; the
 * highlight sets `data-agent-highlight` on it for a moment (css/index.css).
 * A page that has not marked the record still opens; nothing is marked.
 */
function markRecord(recordType: PageRecordType, recordId: string): void {
  if (typeof document === 'undefined') return;
  const element = document.querySelector<HTMLElement>(
    `[data-agent-record="${recordType}:${recordId}"]`,
  );
  if (!element) return;
  element.dataset.agentHighlight = 'true';
  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  window.setTimeout(() => {
    delete element.dataset.agentHighlight;
  }, HIGHLIGHT_MS);
}

export interface AgentUiCommands {
  /** Pass every stream event through; non-command events are ignored. */
  handleEvent: (event: AgentStreamEvent) => void;
  /** Reload the page's data after a confirmed write (confirm runs outside the stream). */
  refresh: () => void;
}

/**
 * For the chat surfaces, which all render inside the app router. Without an
 * Apollo provider (isolated tests) a refresh is simply skipped.
 */
export function useAgentUiCommands(): AgentUiCommands {
  const navigate = useNavigate();
  const apollo = useContext(getApolloContext()).client;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const refresh = useCallback(() => {
    // Refetch whatever the page is showing; no typename list to keep in step.
    void apollo?.refetchQueries({ include: 'active' }).catch(() => undefined);
  }, [apollo]);

  const run = useCallback(
    (command: AgentUiCommand) => {
      if (command.kind === 'refresh') {
        refresh();
        return;
      }
      if (command.kind === 'navigate') {
        if (isNavigablePath(command.path)) navigateRef.current(command.path);
        return;
      }
      if (command.kind === 'highlight') {
        const path = recordPath(command.recordType, command.recordId);
        if (!path) return;
        const onPage = typeof window !== 'undefined' && window.location.pathname.startsWith(path);
        if (!onPage) navigateRef.current(path);
        window.setTimeout(
          () => markRecord(command.recordType, command.recordId),
          onPage ? 0 : HIGHLIGHT_LOOKUP_MS,
        );
      }
    },
    [refresh],
  );

  const handleEvent = useCallback(
    (event: AgentStreamEvent) => {
      if (event.type === 'ui_command') run(event.command);
    },
    [run],
  );

  return useMemo(() => ({ handleEvent, refresh }), [handleEvent, refresh]);
}
